import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import { Icon, type IconName } from "@astrum/ui";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import "./command-palette.css";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

type CommandContext = { navigate: ReturnType<typeof useNavigate>; signOut: () => Promise<void> };

type Command = {
  id: string;
  section: string;
  label: string;
  /** Secondary text — a project code, a job title. */
  hint?: string;
  icon: IconName;
  run: (ctx: CommandContext) => void;
};

/** The unified /search payload — one call, every kind, already visibility-scoped. */
type SearchResponse = {
  results: {
    messages: { id: string; channelId: string; body: string }[];
    channels: { id: string; name: string; kind: string }[];
    projects: { id: string; code: string; name: string }[];
    tasks: { id: string; title: string; projectId: string | null }[];
    files: { id: string; fileName: string }[];
    people: { id: string; displayName: string; jobTitle: string | null }[];
  };
};

/** How long to wait after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 200;

/** Per kind, so one noisy category cannot crowd out the others. */
const MAX_RESULTS_PER_KIND = 5;

/** Turn a `/search` results object into palette commands. */
function toCommands(r: SearchResponse["results"]): Command[] {
  return [
    ...r.tasks.slice(0, MAX_RESULTS_PER_KIND).map((task) => ({
      id: `task-${task.id}`,
      section: "Tasks",
      label: task.title,
      icon: "check" as IconName,
      run: ({ navigate: go }: CommandContext) => go(`/work/task/${task.id}`),
    })),
    ...r.projects.slice(0, MAX_RESULTS_PER_KIND).map((project) => ({
      id: `project-${project.id}`,
      section: "Projects",
      label: project.name,
      hint: project.code,
      icon: "work" as IconName,
      run: ({ navigate: go }: CommandContext) => go(`/work/project/${project.id}`),
    })),
    ...r.channels.slice(0, MAX_RESULTS_PER_KIND).map((channel) => ({
      id: `channel-${channel.id}`,
      section: "Channels",
      label: channel.name,
      icon: "chat" as IconName,
      run: ({ navigate: go }: CommandContext) => go(`/chat/${channel.id}`),
    })),
    ...r.messages.slice(0, MAX_RESULTS_PER_KIND).map((message) => ({
      id: `message-${message.id}`,
      section: "Messages",
      // A one-line preview of the body, trimmed so it fits a row.
      label: message.body.length > 80 ? `${message.body.slice(0, 80)}…` : message.body,
      icon: "chat" as IconName,
      run: ({ navigate: go }: CommandContext) => go(`/chat/${message.channelId}`),
    })),
    ...r.files.slice(0, MAX_RESULTS_PER_KIND).map((file) => ({
      id: `file-${file.id}`,
      section: "Files",
      label: file.fileName,
      icon: "document" as IconName,
      run: ({ navigate: go }: CommandContext) => go(`/files?q=${encodeURIComponent(file.fileName)}`),
    })),
    ...r.people.slice(0, MAX_RESULTS_PER_KIND).map((person) => ({
      id: `person-${person.id}`,
      section: "People",
      label: person.displayName,
      hint: person.jobTitle ?? undefined,
      icon: "people" as IconName,
      run: ({ navigate: go }: CommandContext) => go(`/people?person=${person.id}`),
    })),
  ];
}

// Fixed actions. Live results — tasks, projects, people — are fetched as you
// type and rendered above these, because a name you are looking for beats a
// destination you could have clicked in the rail.
const COMMANDS: Command[] = [
  { id: "go-chat", section: "Go to", label: "Chat", icon: "chat", run: ({ navigate }) => navigate("/chat") },
  { id: "go-work", section: "Go to", label: "Work", icon: "work", run: ({ navigate }) => navigate("/work") },
  {
    id: "go-insights",
    section: "Go to",
    label: "Insights",
    icon: "insights",
    run: ({ navigate }) => navigate("/insights"),
  },
  {
    id: "go-goals",
    section: "Go to",
    label: "Goals",
    icon: "check",
    run: ({ navigate }) => navigate("/goals"),
  },
  {
    id: "go-schedule",
    section: "Go to",
    label: "Schedule",
    icon: "calendar",
    run: ({ navigate }) => navigate("/schedule"),
  },
  { id: "go-people", section: "Go to", label: "People", icon: "people", run: ({ navigate }) => navigate("/people") },
  { id: "go-org-chart", section: "Go to", label: "Org chart", icon: "people", run: ({ navigate }) => navigate("/people/org-chart") },
  {
    id: "new-task",
    section: "Create",
    label: "New task",
    icon: "plus",
    run: ({ navigate }) => navigate("/work?new=task"),
  },
  {
    id: "new-project",
    section: "Create",
    label: "New project",
    icon: "plus",
    run: ({ navigate }) => navigate("/work?new=project"),
  },
  {
    id: "new-channel",
    section: "Create",
    label: "New channel",
    icon: "plus",
    run: ({ navigate }) => navigate("/chat?new=channel"),
  },
  {
    id: "sign-out",
    section: "Account",
    label: "Sign out",
    icon: "settings",
    run: ({ signOut }) => void signOut(),
  },
];

type CommandPaletteState = { open: () => void; close: () => void };

const CommandPaletteContext = createContext<CommandPaletteState | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [results, setResults] = useState<Command[]>([]);

  const navigate = useNavigate();
  const { signOut } = useAuth();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // ⌘K / Ctrl+K toggles globally, from anywhere in the app — this is the one
  // listener every other trigger (rail button, etc.) defers to.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [isOpen]);

  // Focus trap + Escape-to-close + focus restore. The search input is always
  // the natural focus target on open (it's autofocused separately below), so
  // this only needs to keep Tab from escaping the dialog and put focus back
  // on whatever opened the palette once it closes.
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    triggerRef.current = document.activeElement as HTMLElement | null;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      if (triggerRef.current && document.contains(triggerRef.current)) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  /*
   * Live results.
   *
   * Debounced and abandoned on change: typing "rep" fires one request, not
   * three, and a slow earlier response cannot overwrite a newer one.
   */
  useEffect(() => {
    const needle = query.trim();
    if (!isOpen || needle.length < 2) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      const encoded = encodeURIComponent(needle);
      // One unified call: the server searches every kind at once, already
      // visibility-scoped, so the palette no longer fans out to three endpoints
      // and filters projects by hand. Channels, messages and files come along
      // for free — the old fan-out could not reach them.
      //
      // A failed search (offline, or a dead connection) simply shows no live
      // results; the fixed commands below still match.
      api
        .get<SearchResponse>(`/search?q=${encoded}&limit=${MAX_RESULTS_PER_KIND}`)
        .then((res) => {
          if (!cancelled) setResults(toCommands(res.results));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, isOpen]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return COMMANDS;
    // Live results first: a name you are hunting for beats a fixed action
    // that merely happens to contain the same letters.
    const seeAll: Command = {
      id: "search-all",
      section: "Search",
      label: `See all results for "${query.trim()}"`,
      icon: "search",
      run: ({ navigate: go }) => go(`/search?q=${encodeURIComponent(query.trim())}`),
    };
    return [...results, ...COMMANDS.filter((command) => command.label.toLowerCase().includes(needle)), seeAll];
  }, [query, results]);

  // Grouped in-order rather than re-sorted, so "Go to" always precedes
  // "Create" precedes "Account" regardless of which items survive the filter.
  const sections = useMemo(() => {
    const bySection = new Map<string, Command[]>();
    for (const command of filtered) {
      const list = bySection.get(command.section) ?? [];
      list.push(command);
      bySection.set(command.section, list);
    }
    return [...bySection.entries()];
  }, [filtered]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const runCommand = useCallback(
    (command: Command) => {
      command.run({ navigate, signOut });
      close();
    },
    [navigate, signOut, close]
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      } else if (event.key === "Enter") {
        event.preventDefault();
        const command = filtered[activeIndex];
        if (command) runCommand(command);
      }
    },
    [filtered, activeIndex, runCommand]
  );

  // Keep the highlighted row visible as arrow keys move past what's currently
  // scrolled into view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const value = useMemo(() => ({ open, close }), [open, close]);

  let rowIndex = -1;

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      {isOpen && (
        <div
          className="cmdk-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div ref={containerRef} className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
            <div className="cmdk-search">
              <Icon name="search" size={16} />
              <input
                ref={inputRef}
                className="cmdk-input"
                placeholder="Search or jump to…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Search commands"
                aria-activedescendant={filtered[activeIndex] ? `cmdk-${filtered[activeIndex].id}` : undefined}
                role="combobox"
                aria-expanded="true"
                aria-controls="cmdk-list"
                autoComplete="off"
              />
              <kbd className="cmdk-kbd">Esc</kbd>
            </div>

            <div className="cmdk-list" id="cmdk-list" ref={listRef} role="listbox">
              {sections.length === 0 && <p className="cmdk-empty">No matching commands.</p>}
              {sections.map(([section, commands]) => (
                <div className="cmdk-section" key={section}>
                  <p className="cmdk-section-title">{section}</p>
                  {commands.map((command) => {
                    rowIndex += 1;
                    const isActive = rowIndex === activeIndex;
                    return (
                      <button
                        key={command.id}
                        id={`cmdk-${command.id}`}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive || undefined}
                        className={`cmdk-item ${isActive ? "is-active" : ""}`}
                        onMouseEnter={() => setActiveIndex(rowIndex)}
                        onClick={() => runCommand(command)}
                      >
                        <Icon name={command.icon} size={16} />
                        <span className="cmdk-item__label">{command.label}</span>
                        {command.hint && <span className="cmdk-item__hint">{command.hint}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteState {
  const context = useContext(CommandPaletteContext);
  if (!context) throw new Error("useCommandPalette must be used inside CommandPaletteProvider");
  return context;
}
